import { getFullUrl } from '@/lib/config'

export type EmailType =
  | 'welcome'
  | 'application_received'
  | 'application_accepted'
  | 'application_rejected'
  | 'property_verified'
  | 'property_rejected'
  | 'password_reset'
  | 'verification_required'

// Minimal shapes to avoid using any
type MinimalUser = {
  email?: string
  role?: string
  is_verified?: boolean
  kyc_data?: { firstName?: string } | Record<string, unknown>
}

type MinimalProperty = {
  id?: string
  title?: string
  location?: string
  price?: number
}

type MinimalApplication = {
  created_at: string
  client: MinimalUser
  property: MinimalProperty & { landlord?: MinimalUser }
}

function getDisplayName(user: MinimalUser | undefined | null, fallback: string) {
  if (!user) return fallback
  const firstName = (user.kyc_data as { firstName?: string } | undefined)?.firstName
  if (firstName) return firstName
  if (user.email) return user.email.split('@')[0] || fallback
  return fallback
}

export class EmailService {
  static async sendEmail(
    type: EmailType,
    to: string,
    data: Record<string, string>,
    options?: { priority?: 'high' | 'normal' }
  ): Promise<{ success: boolean; mocked?: boolean }> {
    const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/auth/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, to, data, options }),
    })

    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string }
      throw new Error(err?.error || `Failed to send email: ${res.status}`)
    }

    return (await res.json()) as { success: boolean; mocked?: boolean }
  }

  static async sendWelcomeEmail(user: MinimalUser) {
    const to = user.email || ''
    const data = {
      name: getDisplayName(user, 'User'),
      email: user.email || '',
      role: user.role || 'user',
      status: user.is_verified ? 'Verified' : 'Pending Verification',
      dashboardUrl: getFullUrl(`/dashboard/${user.role || 'client'}`),
    }
    return this.sendEmail('welcome', to, data)
  }

  static async sendApplicationReceivedEmail(application: MinimalApplication, landlord: MinimalUser) {
    const to = landlord.email || ''
    const data = {
      landlordName: getDisplayName(landlord, 'Landlord'),
      propertyTitle: application.property.title || 'Property',
      applicantName: getDisplayName(application.client, 'Applicant'),
      applicantEmail: application.client.email || '',
      applicationDate: new Date(application.created_at).toLocaleDateString(),
      applicationUrl: getFullUrl('/dashboard/landlord?tab=applications'),
    }
    return this.sendEmail('application_received', to, data)
  }

  static async sendApplicationStatusEmail(application: MinimalApplication, status: 'accepted' | 'rejected') {
    const to = application.client.email || ''
    const templateType: EmailType = status === 'accepted' ? 'application_accepted' : 'application_rejected'
    const data = {
      applicantName: getDisplayName(application.client, 'Applicant'),
      propertyTitle: application.property.title || 'Property',
      propertyLocation: application.property.location || 'Location',
      propertyPrice: String(application.property.price ?? 0),
      landlordName: getDisplayName(application.property.landlord, 'Landlord'),
      chatUrl: getFullUrl('/dashboard/client?tab=applications'),
      browseUrl: getFullUrl('/dashboard/client'),
    }
    return this.sendEmail(templateType, to, data)
  }

  static async sendPropertyVerificationEmail(property: MinimalProperty, landlord: MinimalUser, verified: boolean) {
    const to = landlord.email || ''
    const templateType: EmailType = verified ? 'property_verified' : 'property_rejected'
    const data = {
      landlordName: getDisplayName(landlord, 'Landlord'),
      propertyTitle: property.title || 'Property',
      propertyLocation: property.location || 'Location',
      propertyPrice: String(property.price ?? 0),
      propertyUrl: getFullUrl(`/property/${property.id || ''}`),
      editUrl: getFullUrl('/dashboard/landlord?tab=properties'),
      rejectionReason: 'Please check property details and images',
    }
    return this.sendEmail(templateType, to, data)
  }

  static async sendPasswordResetEmail(user: MinimalUser, resetToken: string) {
    const to = user.email || ''
    const data = {
      name: getDisplayName(user, 'User'),
      resetUrl: getFullUrl(`/auth/reset-password?token=${resetToken}`),
    }
    return this.sendEmail('password_reset', to, data)
  }

  static async sendVerificationRequiredEmail(user: MinimalUser) {
    const to = user.email || ''
    const data = {
      name: getDisplayName(user, 'User'),
      profileUrl: getFullUrl(`/dashboard/${user.role || 'client'}?tab=profile`),
    }
    return this.sendEmail('verification_required', to, data)
  }
}
